import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback
} from 'react';

const LANGUAGE_STORAGE_KEY = 'lamara_language';
const SUPPORTED_LANGUAGES = ['nl', 'de'];

const translations = {
  nl: {
    nav: {
      home: 'Home',
      overOns: 'Over ons',
      menu: 'Menu',
      reserveren: 'Reserveren',
      contact: 'Contact'
    },
    common: {
      close: 'Sluiten',
      scrollTopLabel: 'Terug naar boven',
      languageSwitcherLabel: 'Taal',
      mapTitle: 'Locatie van Lamara Shisha Lounge',
      contact: {
        addressLines: ['Gewerbering 28', '47623 Kevelaer', 'Duitsland'],
        phone: '+31 6 1860 1865',
        email: 'info@lamarashisha.de'
      },
      openingHours: [
        { day: 'Maandag', time: '16:00-01:00' },
        { day: 'Dinsdag', time: '16:00-01:00' },
        { day: 'Woensdag', time: '16:00-01:00' },
        { day: 'Donderdag', time: '16:00-01:00' },
        { day: 'Vrijdag', time: '16:00-02:00' },
        { day: 'Zaterdag', time: '16:00-02:00' },
        { day: 'Zondag', time: 'Gesloten' }
      ]
    },
    footer: {
      tagline: 'Premium Shisha Experience',
      navigationTitle: 'Navigatie',
      contactTitle: 'Contact',
      hoursTitle: 'Openingstijden',
      rights: 'Alle rechten voorbehouden.'
    },
    home: {
      welcomeTitle: 'Welkom bij Lamara Lounge!',
      welcomeSubtitle:
        'De ultieme shisha ervaring waar je even helemaal kunt ontspannen en genieten. Ontdek unieke smaken, relaxte vibes en een plek waar je je thuis voelt. Kom langs, proef de sfeer en laat de avond beginnen!',
      buttons: {
        reserveren: 'Reserveren',
        menu: 'Menu'
      },
      about: {
        title: 'Over Lamara',
        body:
          'Bij Lamara draait alles om sfeer, kwaliteit en beleving. Of je nu komt ontspannen met vrienden of een speciale avond plant — wij zorgen voor een onvergetelijke ervaring.',
        link: 'Meer over ons'
      },
      iconCards: [
        {
          title: 'Gezellige Sfeer',
          description:
            'Warm interieur en ontspannen ambiance, zodat je je direct thuis voelt.',
          alt: 'Gezellige sfeer in onze lounge',
          image: 'Couch.png'
        },
        {
          title: 'Kwaliteits Shisha',
          description:
            'Geniet van premium smaken en een authentieke shisha-ervaring.',
          alt: 'Premium shisha',
          image: 'Shisha.png'
        },
        {
          title: 'Heerlijke Hapjes & Drankjes',
          description:
            'Laat je verrassen door onze smaakvolle bites en verfrissende drankjes.',
          alt: 'Hapjes en drankjes',
          image: 'Food.png'
        }
      ],
      experienceTitle: 'Onze Beleving',
      experienceDescription:
        'Hemelse zoetigheden en een sfeervolle ambiance wachten op je in onze lounge. Opium lounge-ervaringen onvergetelijk!'
    },
    overOns: {
      heroTitle: 'Over Lamara Lounge',
      heroSubtitle:
        'Luxe ontspanning, heerlijke shisha en de perfecte plek voor elke gelegenheid.',
      paragraphs: [
        'Onze lounge staat bekend om de comfortabele zitplekken en grote tv\'s. Proef onze verrukkelijke smoothies, drankjes en gerechten terwijl je geniet van authentieke shisha\'s in talloze smaken.',
        'Dankzij de gunstige ligging heeft niemand last van geluid. De lounge is bovendien te huur voor verjaardagen, feesten en andere events.'
      ],
      highlights: [
        { title: 'Luxe Zitplekken', alt: 'Luxe zitplekken', image: 'Couch.png' },
        { title: 'Heerlijk Eten & Drinken', alt: 'Eten en drinken', image: 'Food.png' },
        { title: 'Authentieke Shisha', alt: 'Authentieke shisha', image: 'Shisha.png' }
      ]
    },
    menu: {
      welcomeTitle: 'Ons Menu',
      welcomeSubtitle:
        'Ontdek onze heerlijke gerechten, verfrissende drankjes en premium shisha\'s',
      ctaHeading: 'Reserveren voor een Verjaardag, Feest of Grote Groepen?',
      ctaBody: 'Wij maken er graag een onvergetelijke ervaring van!',
      ctaButton: 'Maak Nu een Reservering',
      scrollTopLabel: 'Terug naar boven',
      sections: [
        {
          id: 'warme-dranken',
          title: 'Warme dranken',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Espresso', price: '€3,50' },
                { name: 'Koffie', price: '€3,50' },
                { name: 'Cappuccino', price: '€4,00' },
                { name: 'Thee', price: '€3,00' },
                { name: 'Marokkaanse thee', price: '€5,00' }
              ]
            }
          ]
        },
        {
          id: 'mojito',
          title: 'Mojito',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Pomegranate', price: '€7,00' },
                { name: 'Cloudy Lemonade Base', price: '€7,00' },
                { name: 'Orange', price: '€7,00' },
                { name: 'Green Apple', price: '€7,00' },
                { name: 'Blackberry', price: '€7,00' },
                { name: 'Pineapple', price: '€7,00' },
                { name: 'Tangerine', price: '€7,00' },
                { name: 'Mango', price: '€7,00' },
                { name: 'Watermelon', price: '€7,00' }
              ]
            }
          ]
        },
        {
          id: 'alcoholische-dranken',
          title: 'Alcoholische dranken',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          specialNote: ['Flessen worden geserveerd met 2 drankjes naar keuze.'],
          subSections: [
            {
              title: 'Mojito (met alcohol)',
              items: [
                { name: 'Strawberry Mojito', price: '€8,00' },
                { name: 'Passionfruit Mojito', price: '€8,00' },
                { name: 'Blue Mojito', price: '€8,00' },
                { name: 'Blueberry Mojito', price: '€8,00' }
              ]
            },
            {
              title: 'Sterke drank',
              items: [
                { name: 'Vodka', price: '€5,00' },
                { name: 'Whisky', price: '€5,00' },
                { name: 'Bacardi', price: '€6,00' },
                { name: 'Shots', price: '€4,00' }
              ]
            },
            {
              title: 'Mixdrankjes',
              items: [
                { name: 'Vodka Red Bull', price: '€7,00' },
                { name: 'Whisky Cola', price: '€7,00' }
              ]
            },
            {
              title: 'Bier',
              items: [{ name: 'Heineken', price: '€3,50' }]
            },
            {
              title: 'Flessenservice',
              items: [
                { name: 'Fles Vodka', price: '€80,00' },
                { name: 'Fles Whisky', price: '€80,00' }
              ]
            }
          ]
        },
        {
          id: 'milkshake',
          title: 'Milkshakes',
          backgroundClass: 'shakes-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Aardbei', price: '€7,50' },
                { name: 'Banaan', price: '€7,50' },
                { name: 'Avocado', price: '€7,50' },
                { name: 'Chocolade', price: '€7,50' }
              ]
            }
          ]
        },
        {
          id: 'smoothies',
          title: 'Smoothies',
          backgroundClass: 'smoothies-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Sunshine', price: '€7,50' },
                { name: 'Paradise', price: '€7,50' },
                { name: 'Tropical', price: '€7,50' },
                { name: 'Blueberry', price: '€7,50' }
              ]
            }
          ]
        },
        {
          id: 'burger',
          title: 'Burgers',
          backgroundClass: 'food-section',
          imageCount: 2,
          subSections: [
            {
              items: [
                { name: 'Hamburger', price: '€14,00' },
                {
                  name: 'Cheeseburger',
                  price: '€15,00',
                  images: ['/assets/images/menu/burger1.png', '/assets/images/menu/burger2.png']
                },
                {
                  name: 'Kipburger',
                  price: '€13,00',
                  images: [
                    '/assets/images/menu/chickenburger1.png',
                    '/assets/images/menu/chickenburger2.png'
                  ]
                },
                {
                  name: 'Kip-cheeseburger',
                  price: '€14,00',
                  images: [
                    '/assets/images/menu/chickenburger1.png',
                    '/assets/images/menu/chickenburger2.png'
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'toast',
          title: 'Toast',
          backgroundClass: 'food-section',
          imageCount: 2,
          subSections: [
            {
              items: [
                { name: 'Kaastoast', price: '€4,00' },
                { name: 'Salamitoast', price: '€5,50' },
                { name: 'Toast met kaas en ham', price: '€6,50' },
                { name: 'Pesto-toast', price: '€5,00' }
              ]
            }
          ]
        },
        {
          id: 'dessert',
          title: 'Dessert',
          backgroundClass: 'food-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Baklava', description: '2 stuks', price: '€3,00' },
                { name: 'Cheesecake', price: '€5,50' },
                { name: 'Chocolade-lavacake', price: '€5,50' },
                { name: 'Crepe', price: '€7,00' },
                { name: 'Red velvet cake', price: '€5,50' },
                { name: 'Arabisch ijs', price: '€6,00' },
                { name: 'Fruitsalade', price: '€12,00' }
              ]
            }
          ]
        },
        {
          id: 'shisha',
          title: 'Shisha smaken',
          backgroundClass: 'shisha-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Appel', price: '€18,00' },
                { name: 'Appel-Munt', price: '€18,00' },
                { name: 'Mi Amore', price: '€18,00' },
                { name: 'Love 66', price: '€18,00' },
                { name: 'Lady Killer', price: '€18,00' },
                { name: 'Blue Ice', price: '€18,00' },
                { name: 'Blue Mist', price: '€18,00' },
                { name: 'Kaugummi-Zumt', price: '€18,00' }
              ]
            }
          ]
        },
        {
          id: 'drankenkaart',
          title: 'Drankenkaart',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Cola', price: '€3,00' },
                { name: 'Cola Light', price: '€3,00' },
                { name: 'Cola Zero', price: '€3,00' },
                { name: 'Fanta', price: '€3,00' },
                { name: 'Sprite', price: '€3,00' },
                { name: 'Sprite Zero', price: '€3,00' },
                { name: 'Cassis', price: '€3,00' },
                { name: 'Groene ijsthee', price: '€3,00' },
                { name: 'Perzik ijsthee', price: '€3,00' },
                { name: 'Redbull', price: '€3,50' },
                { name: 'Redbull Zero', price: '€3,50' },
                { name: 'Fernandes Super Pineapple', price: '€3,00' },
                { name: 'Fernandes Cherry Bouquet', price: '€3,00' },
                { name: 'Fernandes Green Punch', price: '€3,00' },
                { name: 'Oasis', price: '€3,00' },
                { name: 'AA Drink', price: '€3,00' },
                { name: 'Plat water', price: '€2,50' },
                { name: 'Mineraalwater', price: '€2,50' },
                { name: 'Fristi', price: '€3,50' },
                { name: 'Chocolademelk', price: '€3,50' }
              ]
            }
          ]
        },
        {
          id: 'snacks',
          title: 'Snacks',
          backgroundClass: 'food-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Chicken nuggets', description: '8 stuks', price: '€5,00' },
                {
                  name: 'Hot wings / Chicken wings (pikant)',
                  description: '5 stuks',
                  price: '€7,00'
                },
                {
                  name: 'Chicken tenders / Kipreepjes',
                  description: '4 stuks',
                  price: '€5,00',
                  imageCount: 2,
                  images: ['/assets/images/menu/strips1.png', '/assets/images/menu/strips2.png']
                },
                { name: 'Friet', price: '€4,00' },
                { name: 'Kaassticks', description: '5 stuks', price: '€6,00' },
                { name: 'Kaassouffle', price: '€3,00' },
                {
                  name: 'Falafel',
                  description: '4 stuks',
                  price: '€7,00',
                  imageCount: 2,
                  images: ['/assets/images/menu/falafel1.png', '/assets/images/menu/falafel2.png']
                },
                { name: 'Vegetarische loempia', description: '7 stuks', price: '€5,00' }
              ]
            }
          ]
        }
      ]
    },
    eventsMenu: {
      title: 'Evenementenmenu',
      message: 'Hier vind je binnenkort ons speciale aanbod voor feesten en evenementen.'
    },
    reserveren: {
      heroTitle: 'Reserveer je Tafel',
      heroSubtitle:
        'Wil je zeker zijn van een plekje in onze lounge? Reserveer nu je tafel en geniet van een onvergetelijke avond vol ontspanning, heerlijke shisha en geweldige sfeer.',
      formTitle: 'Maak je Reservering',
      fields: {
        naam: 'Naam *',
        email: 'Email *',
        telefoon: 'Telefoon *',
        aantalPersonen: 'Aantal Personen *',
        datum: 'Datum *',
        tijd: 'Tijd *',
        opmerkingen: 'Bijzondere Wensen'
      },
      placeholders: {
        opmerkingen:
          'Bijv. verjaardag, allergie informatie, voorkeur voor tafel...'
      },
      submit: {
        idle: 'Reservering Plaatsen',
        loading: 'Bezig met verzenden...'
      },
      infoCards: [
        {
          title: 'Openingstijden',
          lines: [
            'Maandag: 16:00-01:00',
            'Dinsdag: 16:00-01:00',
            'Woensdag: 16:00-01:00',
            'Donderdag: 16:00-01:00',
            'Vrijdag: 16:00-02:00',
            'Zaterdag: 16:00-02:00',
            'Zondag: Gesloten'
          ]
        },
        {
          title: 'Reserveringsbeleid',
          lines: [
            'Reserveringen zijn geldig voor 2 uur.',
            'Voor groepen van 10+ personen nemen we graag persoonlijk contact op om alles perfect te regelen.'
          ]
        },
        {
          title: 'Contact',
          lines: [
            'Telefoon: +31 6 1860 1865',
            'Email: info@lamarashisha.de',
            'Adres: Gewerbering 28, 47623 Kevelaer, Duitsland'
          ]
        }
      ],
      success: {
        title: 'Reservering Ontvangen!',
        message:
          'Bedankt voor je reservering! We hebben je aanvraag ontvangen en nemen binnen 24 uur contact met je op om de reservering te bevestigen.'
      },
      errorAlert:
        'Er is een fout opgetreden. Probeer het opnieuw of neem contact met ons op.'
    },
    contact: {
      heroTitle: 'Neem contact op',
      heroSubtitle:
        'Heb je vragen, suggesties of wil je meer informatie? Wij staan voor je klaar! Vul het onderstaande formulier in en we nemen zo snel mogelijk contact met je op.',
      formTitle: 'Stuur ons een bericht',
      fields: {
        naam: 'Naam *',
        email: 'Email *',
        onderwerp: 'Onderwerp *',
        bericht: 'Bericht *'
      },
      placeholders: {
        naam: 'Jouw naam',
        email: 'jouw@email.nl',
        onderwerp: 'Waar gaat je bericht over?',
        bericht: 'Typ hier je bericht...'
      },
      submit: {
        idle: 'Verstuur Bericht',
        loading: 'Bericht wordt verzonden...'
      },
      infoCards: {
        locationTitle: 'Locatie',
        hoursTitle: 'Openingstijden',
        contactTitle: 'Contact'
      },
      contactLabels: {
        phone: 'Tel',
        email: 'Email'
      },
      success: {
        title: 'Bericht Verzonden!',
        message:
          'Bedankt voor je bericht! We hebben je bericht ontvangen en nemen zo snel mogelijk contact met je op.'
      },
      errorAlert:
        'Er is een fout opgetreden. Probeer het opnieuw of neem telefonisch contact met ons op.',
      mapTitle: 'Lamara Shisha Lounge Location'
    },
    meta: {
      home: {
        title: 'Lamara Shisha Lounge | Home',
        description:
          'Ontdek de ultieme shisha ervaring in onze lounge in Kevelaer.'
      },
      overOns: {
        title: 'Over ons - Lamara Shisha Lounge',
        description:
          'Leer meer over onze lounge, de sfeer en onze unieke shisha beleving.'
      },
      menu: {
        title: 'Menu - Lamara Shisha Lounge',
        description: 'Bekijk ons gevarieerde aanbod van gerechten en shisha smaken.'
      },
      eventsMenu: {
        title: 'Events Menu - Lamara Shisha Lounge',
        description: 'Bekijk de specials voor evenementen bij Lamara.'
      },
      reserveren: {
        title: 'Reserveren - Lamara Shisha Lounge',
        description:
          'Reserveer een tafel bij Lamara Shisha Lounge voor een onvergetelijke avond.'
      },
      contact: {
        title: 'Contact - Lamara Shisha Lounge',
        description:
          'Neem contact op met Lamara Shisha Lounge voor vragen of reserveringen.'
      }
    }
  },
  de: {
    nav: {
      home: 'Startseite',
      overOns: 'Über uns',
      menu: 'Speisekarte',
      reserveren: 'Reservieren',
      contact: 'Kontakt'
    },
    common: {
      close: 'Schließen',
      scrollTopLabel: 'Nach oben',
      languageSwitcherLabel: 'Sprache',
      mapTitle: 'Standort von Lamara Shisha Lounge',
      contact: {
        addressLines: ['Gewerbering 28', '47623 Kevelaer', 'Deutschland'],
        phone: '+31 6 1860 1865',
        email: 'info@lamarashisha.de'
      },
      openingHours: [
        { day: 'Montag', time: '16:00-01:00' },
        { day: 'Dienstag', time: '16:00-01:00' },
        { day: 'Mittwoch', time: '16:00-01:00' },
        { day: 'Donnerstag', time: '16:00-01:00' },
        { day: 'Freitag', time: '16:00-02:00' },
        { day: 'Samstag', time: '16:00-02:00' },
        { day: 'Sonntag', time: 'Geschlossen' }
      ]
    },
    footer: {
      tagline: 'Premium Shisha Experience',
      navigationTitle: 'Navigation',
      contactTitle: 'Kontakt',
      hoursTitle: 'Öffnungszeiten',
      rights: 'Alle Rechte vorbehalten.'
    },
    home: {
      welcomeTitle: 'Willkommen in der Lamara Lounge!',
      welcomeSubtitle:
        'Das ultimative Shisha-Erlebnis, bei dem du entspannen und genießen kannst. Entdecke einzigartige Geschmäcker, entspannte Vibes und eine Atmosphäre, in der du dich sofort wohlfühlst. Komm vorbei, spüre die Stimmung und lass den Abend beginnen!',
      buttons: {
        reserveren: 'Reservieren',
        menu: 'Speisekarte'
      },
      about: {
        title: 'Über Lamara',
        body:
          'Bei Lamara dreht sich alles um Atmosphäre, Qualität und Erlebnis. Ob mit Freunden entspannen oder einen besonderen Abend planen – wir sorgen für eine unvergessliche Zeit.',
        link: 'Mehr über uns'
      },
      iconCards: [
        {
          title: 'Gemütliche Atmosphäre',
          description:
            'Warmes Interieur und entspannte Stimmung, damit du dich sofort zu Hause fühlst.',
          alt: 'Gemütliche Atmosphäre in der Lounge',
          image: 'Couch.png'
        },
        {
          title: 'Premium Shisha',
          description:
            'Genieße premium Geschmäcker und eine authentische Shisha-Erfahrung.',
          alt: 'Premium Shisha',
          image: 'Shisha.png'
        },
        {
          title: 'Leckere Snacks & Drinks',
          description:
            'Lass dich von unseren geschmackvollen Bites und erfrischenden Drinks überraschen.',
          alt: 'Snacks und Drinks',
          image: 'Food.png'
        }
      ],
      experienceTitle: 'Unser Erlebnis',
      experienceDescription:
        'Himmlische Köstlichkeiten und eine stimmungsvolle Atmosphäre warten in unserer Lounge auf dich. Opium Lounge-Erlebnisse unvergesslich!'
    },
    overOns: {
      heroTitle: 'Über Lamara Lounge',
      heroSubtitle:
        'Luxuriöse Entspannung, köstliche Shisha und der perfekte Platz für jeden Anlass.',
      paragraphs: [
        'Unsere Lounge ist bekannt für bequeme Sitzbereiche und große TVs. Probiere unsere leckeren Smoothies, Drinks und Gerichte, während du authentische Shishas in zahlreichen Geschmacksrichtungen genießt.',
        'Dank der guten Lage hat niemand Lärm. Die Lounge kann außerdem für Geburtstage, Feiern und andere Events gemietet werden.'
      ],
      highlights: [
        { title: 'Luxuriöse Sitzplätze', alt: 'Luxuriöse Sitzbereiche', image: 'Couch.png' },
        { title: 'Leckeres Essen & Getränke', alt: 'Essen und Getränke', image: 'Food.png' },
        { title: 'Authentische Shisha', alt: 'Authentische Shisha', image: 'Shisha.png' }
      ]
    },
    menu: {
      welcomeTitle: 'Unsere Speisekarte',
      welcomeSubtitle:
        'Entdecke unsere köstlichen Gerichte, erfrischenden Getränke und premium Shishas',
      ctaHeading: 'Reservieren für Geburtstag, Feier oder große Gruppen?',
      ctaBody: 'Wir sorgen gerne für ein unvergessliches Erlebnis!',
      ctaButton: 'Jetzt reservieren',
      scrollTopLabel: 'Nach oben',
      sections: [
        {
          id: 'warme-dranken',
          title: 'Warme Getränke',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Espresso', price: '€3,50' },
                { name: 'Kaffee', price: '€3,50' },
                { name: 'Cappuccino', price: '€4,00' },
                { name: 'Tee', price: '€3,00' },
                { name: 'Marokkanischer Tee', price: '€5,00' }
              ]
            }
          ]
        },
        {
          id: 'mojito',
          title: 'Mojito',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Pomegranate', price: '€7,00' },
                { name: 'Cloudy Lemonade Base', price: '€7,00' },
                { name: 'Orange', price: '€7,00' },
                { name: 'Green Apple', price: '€7,00' },
                { name: 'Blackberry', price: '€7,00' },
                { name: 'Pineapple', price: '€7,00' },
                { name: 'Tangerine', price: '€7,00' },
                { name: 'Mango', price: '€7,00' },
                { name: 'Watermelon', price: '€7,00' }
              ]
            }
          ]
        },
        {
          id: 'alcoholische-dranken',
          title: 'Alkoholische Getränke',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          specialNote: ['Flaschen werden mit 2 Getränken nach Wahl serviert.'],
          subSections: [
            {
              title: 'Mojito (mit Alkohol)',
              items: [
                { name: 'Strawberry Mojito', price: '€8,00' },
                { name: 'Passionfruit Mojito', price: '€8,00' },
                { name: 'Blue Mojito', price: '€8,00' },
                { name: 'Blueberry Mojito', price: '€8,00' }
              ]
            },
            {
              title: 'Spirituosen',
              items: [
                { name: 'Vodka', price: '€5,00' },
                { name: 'Whisky', price: '€5,00' },
                { name: 'Bacardi', price: '€6,00' },
                { name: 'Shots', price: '€4,00' }
              ]
            },
            {
              title: 'Mixgetränke',
              items: [
                { name: 'Vodka Red Bull', price: '€7,00' },
                { name: 'Whisky Cola', price: '€7,00' }
              ]
            },
            {
              title: 'Bier',
              items: [{ name: 'Heineken', price: '€3,50' }]
            },
            {
              title: 'Flaschenservice',
              items: [
                { name: 'Flasche Vodka', price: '€80,00' },
                { name: 'Flasche Whisky', price: '€80,00' }
              ]
            }
          ]
        },
        {
          id: 'milkshake',
          title: 'Milkshake',
          backgroundClass: 'shakes-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Erdbeer', price: '€7,50' },
                { name: 'Bananen', price: '€7,50' },
                { name: 'Avocado', price: '€7,50' },
                { name: 'Schokoladen', price: '€7,50' }
              ]
            }
          ]
        },
        {
          id: 'smoothies',
          title: 'Smoothies',
          backgroundClass: 'smoothies-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Sunshine', price: '€7,50' },
                { name: 'Paradise', price: '€7,50' },
                { name: 'Tropical', price: '€7,50' },
                { name: 'Blueberry', price: '€7,50' }
              ]
            }
          ]
        },
        {
          id: 'burger',
          title: 'Burger',
          backgroundClass: 'food-section',
          imageCount: 2,
          subSections: [
            {
              items: [
                { name: 'Hamburger', price: '€14,00' },
                {
                  name: 'Cheeseburger',
                  price: '€15,00',
                  images: ['/assets/images/menu/burger1.png', '/assets/images/menu/burger2.png']
                },
                {
                  name: 'Hähnchenburger',
                  price: '€13,00',
                  images: [
                    '/assets/images/menu/chickenburger1.png',
                    '/assets/images/menu/chickenburger2.png'
                  ]
                },
                {
                  name: 'Hähnchen-Cheeseburger',
                  price: '€14,00',
                  images: [
                    '/assets/images/menu/chickenburger1.png',
                    '/assets/images/menu/chickenburger2.png'
                  ]
                }
              ]
            }
          ]
        },
        {
          id: 'toast',
          title: 'Toast',
          backgroundClass: 'food-section',
          imageCount: 2,
          subSections: [
            {
              items: [
                { name: 'Käsetoast', price: '€4,00' },
                { name: 'Salamitoast', price: '€5,50' },
                { name: 'Toast mit Käse und Schinken', price: '€6,50' },
                { name: 'Pesto-Toast', price: '€5,00' }
              ]
            }
          ]
        },
        {
          id: 'dessert',
          title: 'Dessert',
          backgroundClass: 'food-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Baklava', description: '2 stück', price: '€3,00' },
                { name: 'Käsekuchen', price: '€5,50' },
                { name: 'Schokoladen-Lavakuchen', price: '€5,50' },
                { name: 'Crépe', price: '€7,00' },
                { name: 'Red-Velvet-Kuchen', price: '€5,50' },
                { name: 'Arabisches Eis', price: '€6,00' },
                { name: 'Obstsalat', price: '€12,00' }
              ]
            }
          ]
        },
        {
          id: 'shisha',
          title: 'Shisha',
          backgroundClass: 'shisha-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Apfel', price: '€18,00' },
                { name: 'Apfel-Minze', price: '€18,00' },
                { name: 'Mi Amore', price: '€18,00' },
                { name: 'Love 66', price: '€18,00' },
                { name: 'Lady Killer', price: '€18,00' },
                { name: 'Blue Ice', price: '€18,00' },
                { name: 'Blue Mist', price: '€18,00' },
                { name: 'Kaugummi-Zumt', price: '€18,00' }
              ]
            }
          ]
        },
        {
          id: 'getrankekarte',
          title: 'Getränkekarte',
          backgroundClass: 'drinks-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Cola', price: '€3,00' },
                { name: 'Cola Light', price: '€3,00' },
                { name: 'Cola Zero', price: '€3,00' },
                { name: 'Fanta', price: '€3,00' },
                { name: 'Sprite', price: '€3,00' },
                { name: 'Sprite Zero', price: '€3,00' },
                { name: 'Cassis', price: '€3,00' },
                { name: 'Grüner Eistee', price: '€3,00' },
                { name: 'Eistee Pfirsich', price: '€3,00' },
                { name: 'Redbull', price: '€3,50' },
                { name: 'Redbull Zero', price: '€3,50' },
                { name: 'Fernandes Super Pineapple', price: '€3,00' },
                { name: 'Fernandes Cherry Bouquet', price: '€3,00' },
                { name: 'Fernandes Green Punch', price: '€3,00' },
                { name: 'Oasis', price: '€3,00' },
                { name: 'AA Drink', price: '€3,00' },
                { name: 'Stilles Wasser', price: '€2,50' },
                { name: 'Mineralwasser', price: '€2,50' },
                { name: 'Fristi', price: '€3,50' },
                { name: 'Schokomilch', price: '€3,50' }
              ]
            }
          ]
        },
        {
          id: 'snacks',
          title: 'Snacks',
          backgroundClass: 'food-section',
          imageCount: 1,
          subSections: [
            {
              items: [
                { name: 'Chicken Nuggets', description: '8 stück', price: '€5,00' },
                {
                  name: 'Hot Wings / Chicken Wings (scharf)',
                  description: '5 stück',
                  price: '€7,00'
                },
                {
                  name: 'Chickn Tenders / Hähnchenstreifen',
                  description: '4 stück',
                  price: '€5,00',
                  imageCount: 2,
                  images: ['/assets/images/menu/strips1.png', '/assets/images/menu/strips2.png']
                },
                { name: 'Pommes', price: '€4,00' },
                { name: 'Käsesticks', description: '5 stück', price: '€6,00' },
                { name: 'Käsesoufflé', price: '€3,00' },
                {
                  name: 'Falafel',
                  description: '4 stück',
                  price: '€7,00',
                  imageCount: 2,
                  images: ['/assets/images/menu/falafel1.png', '/assets/images/menu/falafel2.png']
                },
                { name: 'Vegetarische Frühlingsrolle', description: '7 stück', price: '€5,00' }
              ]
            }
          ]
        }
      ]
    },
    eventsMenu: {
      title: 'Events Menü',
      message: 'Hier findest du bald unser spezielles Angebot für Feiern und Veranstaltungen.'
    },
    reserveren: {
      heroTitle: 'Reserviere deinen Tisch',
      heroSubtitle:
        'Möchtest du dir einen Platz in unserer Lounge sichern? Reserviere jetzt deinen Tisch und genieße einen unvergesslichen Abend mit Entspannung, köstlicher Shisha und großartiger Stimmung.',
      formTitle: 'Reservierung erstellen',
      fields: {
        naam: 'Name *',
        email: 'Email *',
        telefoon: 'Telefon *',
        aantalPersonen: 'Anzahl Personen *',
        datum: 'Datum *',
        tijd: 'Uhrzeit *',
        opmerkingen: 'Besondere Wünsche'
      },
      placeholders: {
        opmerkingen:
          'z.B. Geburtstag, Allergien, Lieblingsplatz...'
      },
      submit: {
        idle: 'Reservierung Platzieren',
        loading: 'Senden...'
      },
      infoCards: [
        {
          title: 'Öffnungszeiten',
          lines: [
            'Montag: 16:00-01:00',
            'Dienstag: 16:00-01:00',
            'Mittwoch: 16:00-01:00',
            'Donnerstag: 16:00-01:00',
            'Freitag: 16:00-02:00',
            'Samstag: 16:00-02:00',
            'Sonntag: Geschlossen'
          ]
        },
        {
          title: 'Reservierungsbedingungen',
          lines: [
            'Reservierungen gelten für 2 Stunden.',
            'Bei Gruppen ab 10 Personen melden wir uns persönlich, um alles perfekt zu planen.'
          ]
        },
        {
          title: 'Kontakt',
          lines: [
            'Telefon: +31 6 1860 1865',
            'Email: info@lamarashisha.de',
            'Adresse: Gewerbering 28, 47623 Kevelaer, Deutschland'
          ]
        }
      ],
      success: {
        title: 'Reservierung erhalten!',
        message:
          'Danke für deine Reservierung! Wir haben deine Anfrage erhalten und melden uns innerhalb von 24 Stunden zur Bestätigung.'
      },
      errorAlert:
        'Ein Fehler ist aufgetreten. Bitte versuche es erneut oder kontaktiere uns direkt.'
    },
    contact: {
      heroTitle: 'Kontakt aufnehmen',
      heroSubtitle:
        'Hast du Fragen, Anregungen oder möchtest du mehr wissen? Wir sind für dich da! Fülle das Formular aus und wir melden uns schnellstmöglich zurück.',
      formTitle: 'Schick uns eine Nachricht',
      fields: {
        naam: 'Name *',
        email: 'Email *',
        onderwerp: 'Betreff *',
        bericht: 'Nachricht *'
      },
      placeholders: {
        naam: 'Dein Name',
        email: 'dein@email.de',
        onderwerp: 'Worum geht es?',
        bericht: 'Schreibe hier deine Nachricht...'
      },
      submit: {
        idle: 'Nachricht senden',
        loading: 'Nachricht wird gesendet...'
      },
      infoCards: {
        locationTitle: 'Standort',
        hoursTitle: 'Öffnungszeiten',
        contactTitle: 'Kontakt'
      },
      contactLabels: {
        phone: 'Tel',
        email: 'E-Mail'
      },
      success: {
        title: 'Nachricht gesendet!',
        message: 'Danke für deine Nachricht! Wir haben sie erhalten und melden uns so schnell wie möglich.'
      },
      errorAlert:
        'Es ist ein Fehler aufgetreten. Bitte versuche es erneut oder ruf uns direkt an.',
      mapTitle: 'Lamara Shisha Lounge Standort'
    },
    meta: {
      home: {
        title: 'Lamara Shisha Lounge | Startseite',
        description:
          'Entdecke das ultimative Shisha-Erlebnis in unserer Lounge in Kevelaer.'
      },
      overOns: {
        title: 'Über uns - Lamara Shisha Lounge',
        description:
          'Erfahre mehr über unsere Lounge, die Atmosphäre und unsere besondere Shisha-Begegnung.'
      },
      menu: {
        title: 'Speisekarte - Lamara Shisha Lounge',
        description:
          'Sieh dir unser abwechslungsreiches Angebot an Gerichten und Shisha-Geschmäckern an.'
      },
      eventsMenu: {
        title: 'Events Menü - Lamara Shisha Lounge',
        description: 'Entdecke die Specials für Events bei Lamara.'
      },
      reserveren: {
        title: 'Reservieren - Lamara Shisha Lounge',
        description:
          'Reserviere einen Tisch in der Lamara Shisha Lounge für einen unvergesslichen Abend.'
      },
      contact: {
        title: 'Kontakt - Lamara Shisha Lounge',
        description:
          'Kontaktiere Lamara Shisha Lounge bei Fragen oder Reservierungswünschen.'
      }
    }
  }
};

const LanguageContext = createContext(null);

function getNestedValue(object, path) {
  if (!object || !path) return undefined;
  return path.split('.').reduce((current, segment) => {
    if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
      return current[segment];
    }
    return undefined;
  }, object);
}

function getInitialLanguage() {
  if (typeof window === 'undefined') {
    return 'nl';
  }

  const saved = window.localStorage.getItem(LANGUAGE_STORAGE_KEY);
  if (saved && SUPPORTED_LANGUAGES.includes(saved)) {
    return saved;
  }

  return 'nl';
}

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(getInitialLanguage);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    document.documentElement.lang = language === 'de' ? 'de' : 'nl';
  }, [language]);

  const dictionary = translations[language] || translations.nl;

  const t = useCallback(
    (path, fallback = '') => {
      const value = getNestedValue(dictionary, path);
      if (value === undefined || value === null) {
        return fallback;
      }
      return value;
    },
    [dictionary]
  );

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dictionary }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useTranslation() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
}
